import {
    KLING_OMNI_ACTION_TAG,
    actionDisplayNameForModel,
    apiModelToCamel,
    normalizeActionTag,
    type IAiModel,
    type IAiModelApi,
} from './aiModels'
import { DEFAULT_FILM_MODEL, DEFAULT_VIDEO_MODEL } from './defaultAiModels'

export type ActionKind = 'model' | 'tool'
export type KnownToolActionKey =
    | 'extend'
    | 'film'
    | 'gif'
    | 'illustrate'
    | 'pay'
    | 'resize'
    | 'screenshot'
    | 'stitch'
    | 'treesearch'
export type ToolActionKey = KnownToolActionKey | (string & {})
export type ActionSectionId = 'image' | 'video' | 'audio' | 'tools'
export type ActionInputKind = 'text' | 'image' | 'video' | 'audio' | (string & {})
export type ActionDefaultSettings = Record<string, unknown>

export interface ToolActionDefinition {
    id: string
    key: ToolActionKey
    commandTag: string
    humanName: string
    displayName?: string | null
    description: string
    modelType: IAiModel['modelType']
    canRemixVideo?: boolean
    canRemixImage?: boolean
    canRemixAudio?: boolean
    sortPriority?: number
    section?: ActionSectionId
    showInWand?: boolean
    referenceImageLimit?: number | null
    referenceVideoLimit?: number | null
    referenceAudioLimit?: number | null
    defaultAiModelId?: string | null
    defaultAiModelName?: string | null
    supportedInputs?: ActionInputKind[]
    requiredInputs?: string[]
    promptRequired?: boolean
    defaultSettings?: ActionDefaultSettings
}

export interface ActionDefinitionDisplayFields {
    id: string
    name: string
    usdCost: number
    modelType: IAiModel['modelType']
    usdTreechatPrivatePrice: number
    provider: string
    createdAt?: string
    updatedAt?: string
    actionTagName: string
    usdTreechatPublicPrice: number
    humanName: string
    displayName?: string | null
    description: string
    descriptionShort?: string
    canRemixVideo?: boolean
    canRemixImage?: boolean
    canRemixAudio?: boolean
    referenceImageLimit?: number | null
    referenceVideoLimit?: number | null
    referenceAudioLimit?: number | null
    sortPriority?: number
    allowedDurations?: number[] | null
    minDurationSeconds?: number | null
    maxDurationSeconds?: number | null
    defaultDurationSeconds?: number | null
    usdCostPerSecond?: number | null
    usdTreechatPrivatePricePerSecond?: number | null
    usdTreechatPublicPricePerSecond?: number | null
    estimatedProviderCostUsd?: number | null
    estimatedProviderCostPerSecondUsd?: number | null
    pricingMarginBps?: number | null
    pricingEstimatorVersion?: string | null
    pricingTierKey?: string | null
    contextualPricingRules?: IAiModel['contextualPricingRules']
    openclawIntegrationId?: string | null
    openclawOwnerUserId?: string | null
    openclawOwnerUsername?: string | null
    openclawAlias?: string | null
    openclawVisibilityScope?: 'owner' | 'stream' | null
    openclawTeamIds?: string[] | null
}

interface ActionDefinitionBase extends ActionDefinitionDisplayFields {
    kind: ActionKind
    key: string
    commandTag: string
    section?: ActionSectionId
    showInWand?: boolean
    defaultAiModelId?: string | null
    supportedInputs?: ActionInputKind[]
    requiredInputs?: string[]
    promptRequired?: boolean
    defaultSettings?: ActionDefaultSettings
}

export interface ModelActionDefinition extends ActionDefinitionBase {
    kind: 'model'
    aiModel?: IAiModel
    tool?: undefined
    toolKey?: undefined
}

export interface ToolCatalogActionDefinition extends ActionDefinitionBase {
    kind: 'tool'
    aiModel?: IAiModel
    tool: ToolActionDefinition
    toolKey: ToolActionKey
}

export type ActionDefinition = ModelActionDefinition | ToolCatalogActionDefinition

export interface ToolActionDefinitionApi {
    id: string
    key: string
    command_tag: string
    human_name: string
    display_name?: string | null
    description: string
    model_type: IAiModelApi['model_type']
    can_remix_video?: boolean
    can_remix_image?: boolean
    can_remix_audio?: boolean
    sort_priority?: number | null
    section?: string | null
    show_in_wand?: boolean
    reference_image_limit?: number | null
    reference_video_limit?: number | null
    reference_audio_limit?: number | null
    default_ai_model_id?: string | null
    supported_inputs?: string[]
    required_inputs?: string[]
    prompt_required?: boolean
    default_settings?: Record<string, unknown>
}

export interface ActionDefinitionApi extends Partial<IAiModelApi> {
    id: string
    kind: ActionKind
    key?: string
    command_tag?: string
    ai_model_id?: string | null
    default_ai_model_id?: string | null
    tool_key?: string | null
    section?: string | null
    show_in_wand?: boolean
    supported_inputs?: string[]
    required_inputs?: string[]
    prompt_required?: boolean
    default_settings?: Record<string, unknown>
    ai_model?: IAiModelApi | null
    tool?: ToolActionDefinitionApi | null
}

export interface Action {
    id: string
    client_id?: string
    action_definition_id: string
    kind: ActionKind
    tag?: string
    command_text?: string
    ai_model_id?: string | null
    tool_key?: ToolActionKey | null
    inputs?: Record<string, any>
    settings?: Record<string, any>
}

export const TOOL_ACTIONS: ToolActionDefinition[] = [
    {
        id: '6b78e822-155b-4dc7-82fb-47c961328710',
        key: 'illustrate',
        commandTag: 'illustrate',
        humanName: 'Illustrate',
        displayName: 'Illustrate',
        description: 'Create an AI illustration inspired by this post.',
        modelType: 'image',
        canRemixVideo: false,
        canRemixImage: false,
        canRemixAudio: false,
        sortPriority: 100,
        section: 'image',
        showInWand: false,
        defaultAiModelName: 'gemini-flash25',
        supportedInputs: [],
        requiredInputs: [],
        promptRequired: false,
        defaultSettings: {},
    },
    {
        id: '1dc7c69e-5f2d-4ec8-a636-a26462870027',
        key: 'film',
        commandTag: 'film',
        humanName: 'Film',
        displayName: 'Film',
        description: 'Create an AI film inspired by this post.',
        modelType: 'video',
        canRemixVideo: false,
        canRemixImage: false,
        canRemixAudio: false,
        sortPriority: 100,
        section: 'video',
        showInWand: false,
        defaultAiModelName: DEFAULT_FILM_MODEL.name,
        supportedInputs: [],
        requiredInputs: [],
        promptRequired: false,
        defaultSettings: {
            durationSeconds: DEFAULT_FILM_MODEL.durationSeconds,
        },
    },
    {
        id: '4f4d55c5-8e3f-43b9-bbae-1e90f674ef69',
        key: 'extend',
        commandTag: 'extend',
        humanName: 'Extend',
        displayName: 'Extend',
        description: 'Extend an attached video using an image-to-video model.',
        modelType: 'video',
        canRemixVideo: true,
        canRemixImage: false,
        canRemixAudio: false,
        sortPriority: 92,
        section: 'video',
        showInWand: true,
        defaultAiModelName: DEFAULT_VIDEO_MODEL.name,
        supportedInputs: ['text', 'video'],
        requiredInputs: ['video'],
        promptRequired: false,
        defaultSettings: {
            smartStitch: true,
        },
    },
    {
        id: '12187b02-3eba-4c3c-9a7f-983c41fc9a18',
        key: 'gif',
        commandTag: 'gif',
        humanName: 'GIF',
        displayName: 'GIF',
        description: 'Convert an attached video to an animated GIF.',
        modelType: 'image',
        canRemixVideo: true,
        canRemixImage: false,
        canRemixAudio: false,
        sortPriority: 95,
        section: 'tools',
        showInWand: true,
        supportedInputs: ['video'],
        requiredInputs: ['video'],
        promptRequired: false,
        defaultSettings: {
            gifFrameRate: 15,
            gifMaxWidth: 720,
        },
    },
    {
        id: 'ee2baf7f-3b00-4c0e-bc62-9116e5f9cedb',
        key: 'screenshot',
        commandTag: 'screenshot',
        humanName: 'Screenshot',
        displayName: 'Screenshot',
        description: 'Extract a still frame from an attached video.',
        modelType: 'image',
        canRemixVideo: true,
        canRemixImage: false,
        canRemixAudio: false,
        sortPriority: 94,
        section: 'tools',
        showInWand: true,
        supportedInputs: ['video'],
        requiredInputs: ['video'],
        promptRequired: false,
        defaultSettings: {
            screenshotTimestamp: '00:00',
        },
    },
    {
        id: '5e85f861-3d4a-40bc-b135-fd4bc7ef44a7',
        key: 'stitch',
        commandTag: 'stitch',
        humanName: 'Smart Stitch',
        displayName: 'Smart Stitch',
        description: 'Stitch two MP4 videos with transition smoothing.',
        modelType: 'video',
        canRemixVideo: true,
        canRemixImage: false,
        canRemixAudio: false,
        sortPriority: 93,
        section: 'tools',
        showInWand: true,
        supportedInputs: ['video'],
        requiredInputs: ['video-0', 'video-1'],
        promptRequired: false,
        defaultSettings: {},
    },
    {
        id: 'c0f17c17-aa52-497a-898a-efe69c95e4bb',
        key: 'resize',
        commandTag: 'resize',
        humanName: 'Resize',
        displayName: 'Resize',
        description: 'Resize an image to a different size.',
        modelType: 'image',
        section: 'tools',
        showInWand: false,
        supportedInputs: ['image'],
        requiredInputs: ['image'],
        promptRequired: false,
        defaultSettings: {},
    },
    {
        id: '2c77aaba-ad1a-4b6f-8459-3991057412a2',
        key: 'pay',
        commandTag: 'pay',
        humanName: 'pay',
        displayName: 'pay',
        description: 'Send a payment to another Treechat user.',
        modelType: 'text',
        section: 'tools',
        showInWand: false,
        // 'text' makes the pay pill host an inline prompt ("@user 2000 sats") whose content
        // syncs to the pay card. Kept kind:'tool' with no model so it never enters the AI-fee flow.
        supportedInputs: ['text'],
        requiredInputs: [],
        promptRequired: true,
        defaultSettings: {},
    },
    {
        id: 'c7d5f6f3-4abb-4125-8d26-c4914ab22950',
        key: 'treesearch',
        commandTag: 'treesearch',
        humanName: 'Tree Search',
        displayName: 'Tree Search',
        description: 'Write a short report based on a web search.',
        modelType: 'text',
        section: 'tools',
        showInWand: false,
        supportedInputs: ['text'],
        requiredInputs: [],
        promptRequired: true,
        defaultSettings: {},
    },
]

export const FALLBACK_MODEL_ACTION_TAGS = [
    'flux',
    'dalle',
    'sd3',
    'sdl',
    'fluxfast',
    'flash',
    'nb',
    'nbpro',
    'gpt4o',
    'gptimage2',
    'gptimage2.5',
    'gptimage2.5sunburst',
    'grokimage',
    'grokvideo',
    'grokvideo1.5',
    'grok_imagine',
    'imagine',
    'minimax',
    'h3',
    'h3max',
    'kling',
    'kling2',
    'kling3',
    'kling4',
    'luma',
    'veo2',
    'veo3',
    'sora2',
    'wan',
    'seedance',
    'seedance2',
    'seedance2.5',
    'minimaxmusic',
    'suno',
    'qwen',
    'tts',
    'clone',
] as const

const HIDDEN_ACTION_COMMAND_PREFIXES = ['animate', 'edit']
// minimaxmusic: MiniMax closed its Music API to new users (HTTP 410), so every run fails.
const RETIRED_ACTION_COMMAND_TAGS = new Set(['grok', 'seedance1', 'minimaxmusic'])
const TRANSPARENT_ACTION_COMMAND_TAGS = new Set([KLING_OMNI_ACTION_TAG])

export function isHiddenActionCommandTag(tag: string | null | undefined): boolean {
    const normalizedTag = tag?.toString().trim().replace(/^!+/, '').toLowerCase() || ''
    if (TRANSPARENT_ACTION_COMMAND_TAGS.has(normalizedTag)) return true
    if (RETIRED_ACTION_COMMAND_TAGS.has(normalizedTag)) return true

    return HIDDEN_ACTION_COMMAND_PREFIXES.some(
        prefix => normalizedTag === prefix || normalizedTag.startsWith(`${prefix}_`),
    )
}

export function isVisibleActionDefinition(action: ActionDefinition): boolean {
    return !isHiddenActionCommandTag(action.commandTag || action.actionTagName)
}

export function buildActionModalDefinitions(
    actionDefinitions: ActionDefinition[] = [],
): ActionDefinition[] {
    return actionDefinitions.filter(
        action => action.showInWand !== false && isVisibleActionDefinition(action),
    )
}

function isModelType(value: unknown): value is IAiModel['modelType'] {
    return value === 'image' || value === 'video' || value === 'text' || value === 'audio'
}

function normalizeSection(value: unknown): ActionSectionId | undefined {
    if (value === 'image' || value === 'video' || value === 'audio' || value === 'tools') {
        return value
    }

    return undefined
}

function numberValue(value: unknown, fallback = 0): number {
    const number = Number(value)
    return Number.isFinite(number) ? number : fallback
}

function camelizeDefaultSettings(
    settings: Record<string, unknown> | null | undefined,
): ActionDefaultSettings {
    return Object.entries(settings || {}).reduce<ActionDefaultSettings>(
        (camelized, [key, value]) => {
            const camelKey = key.replace(/_([a-z])/g, (_match, letter: string) =>
                letter.toUpperCase(),
            )
            camelized[camelKey] = value
            return camelized
        },
        {},
    )
}

function normalizeActionInputs(inputs: string[] | null | undefined): ActionInputKind[] | undefined {
    return Array.isArray(inputs)
        ? inputs
              .map(input => input?.toString().trim())
              .filter((input): input is ActionInputKind => !!input)
        : undefined
}

function apiModelPayloadToCamel(api: Partial<IAiModelApi> & { id?: string }): IAiModel {
    const id = api.id?.toString() || ''
    const name = api.name?.toString() || id
    const humanName = api.human_name?.toString() || name
    const modelType = isModelType(api.model_type) ? api.model_type : 'text'
    const usdCost = numberValue(api.usd_cost)
    const privatePrice = numberValue(api.usd_treechat_private_price, usdCost)
    const publicPrice = numberValue(api.usd_treechat_public_price, privatePrice)

    return apiModelToCamel({
        id,
        name,
        human_name: humanName,
        display_name: api.display_name,
        description: api.description?.toString() || '',
        usd_cost: usdCost,
        model_type: modelType,
        usd_treechat_private_price: privatePrice,
        provider: api.provider?.toString() || 'local',
        created_at: api.created_at,
        updated_at: api.updated_at,
        action_tag_name: api.action_tag_name?.toString() || name,
        usd_treechat_public_price: publicPrice,
        supported_inputs: api.supported_inputs,
        required_inputs: api.required_inputs,
        can_remix_video: api.can_remix_video,
        can_remix_image: api.can_remix_image,
        can_remix_audio: api.can_remix_audio,
        reference_image_limit: api.reference_image_limit,
        reference_video_limit: api.reference_video_limit,
        reference_audio_limit: api.reference_audio_limit,
        sort_priority: api.sort_priority,
        allowed_durations: api.allowed_durations,
        min_duration_seconds: api.min_duration_seconds,
        max_duration_seconds: api.max_duration_seconds,
        default_duration_seconds: api.default_duration_seconds,
        usd_cost_per_second: api.usd_cost_per_second,
        usd_treechat_private_price_per_second: api.usd_treechat_private_price_per_second,
        usd_treechat_public_price_per_second: api.usd_treechat_public_price_per_second,
        estimated_provider_cost_usd: api.estimated_provider_cost_usd,
        estimated_provider_cost_per_second_usd: api.estimated_provider_cost_per_second_usd,
        pricing_margin_bps: api.pricing_margin_bps,
        pricing_estimator_version: api.pricing_estimator_version,
        pricing_tier_key: api.pricing_tier_key,
        contextual_pricing_rules: api.contextual_pricing_rules,
        description_short: api.description_short,
        openclaw_integration_id: api.openclaw_integration_id,
        openclaw_owner_user_id: api.openclaw_owner_user_id,
        openclaw_owner_username: api.openclaw_owner_username,
        openclaw_alias: api.openclaw_alias,
        openclaw_visibility_scope: api.openclaw_visibility_scope,
        openclaw_team_ids: api.openclaw_team_ids,
    })
}

function toolDefinitionFromApi(
    api: ToolActionDefinitionApi,
    fallbackDefaultAiModelId?: string | null,
): ToolActionDefinition {
    return {
        id: api.id,
        key: api.key,
        commandTag: api.command_tag,
        humanName: api.human_name,
        displayName: api.display_name || null,
        description: api.description,
        modelType: isModelType(api.model_type) ? api.model_type : 'text',
        canRemixVideo: api.can_remix_video,
        canRemixImage: api.can_remix_image,
        canRemixAudio: api.can_remix_audio,
        sortPriority: api.sort_priority ?? undefined,
        section: normalizeSection(api.section),
        showInWand: api.show_in_wand,
        referenceImageLimit: api.reference_image_limit,
        referenceVideoLimit: api.reference_video_limit,
        referenceAudioLimit: api.reference_audio_limit,
        defaultAiModelId: api.default_ai_model_id ?? fallbackDefaultAiModelId ?? null,
        supportedInputs: normalizeActionInputs(api.supported_inputs),
        requiredInputs: api.required_inputs,
        promptRequired: api.prompt_required,
        defaultSettings: camelizeDefaultSettings(api.default_settings),
    }
}

function toolApiFromActionDefinitionApi(api: ActionDefinitionApi): ToolActionDefinitionApi {
    const commandTag = api.command_tag || api.tool_key || api.name || api.id

    return {
        id: api.id,
        key: api.tool_key || api.name || commandTag,
        command_tag: commandTag,
        human_name: api.human_name || api.name || commandTag,
        display_name: api.display_name,
        description: api.description || '',
        model_type: isModelType(api.model_type) ? api.model_type : 'text',
        can_remix_video: api.can_remix_video,
        can_remix_image: api.can_remix_image,
        can_remix_audio: api.can_remix_audio,
        sort_priority: api.sort_priority,
        section: api.section,
        show_in_wand: api.show_in_wand,
        reference_image_limit: api.reference_image_limit,
        reference_video_limit: api.reference_video_limit,
        reference_audio_limit: api.reference_audio_limit,
        default_ai_model_id: api.default_ai_model_id,
        supported_inputs: api.supported_inputs,
        required_inputs: api.required_inputs,
        prompt_required: api.prompt_required,
        default_settings: api.default_settings,
    }
}

function modelTypeForFallbackTag(tag: string): IAiModel['modelType'] {
    if (['minimaxmusic', 'suno', 'tts', 'clone'].includes(tag)) return 'audio'
    if (
        [
            'grokvideo',
            'grokvideo1.5',
            'minimax',
            'h3',
            'h3max',
            'kling',
            'kling2',
            'kling3',
            'kling4',
            'luma',
            'veo2',
            'veo3',
            'sora2',
            'wan',
            'seedance',
            'seedance1',
            'seedance2',
            'seedance2.5',
        ].includes(tag)
    ) {
        return 'video'
    }
    return 'image'
}

function sectionForModelType(modelType: IAiModel['modelType']): ActionSectionId | undefined {
    if (modelType === 'image' || modelType === 'video' || modelType === 'audio') return modelType
    return undefined
}

function actionDefinitionFromModel(model: IAiModel): ActionDefinition {
    return {
        ...model,
        displayName: actionDisplayNameForModel(model, model.actionTagName),
        kind: 'model',
        key: `model:${model.id}`,
        commandTag: model.actionTagName,
        aiModel: model,
        defaultAiModelId: model.id,
        section: sectionForModelType(model.modelType),
        showInWand: true,
    }
}

function actionDefinitionFromTool(
    tool: ToolActionDefinition,
    aiModels: IAiModel[],
): ActionDefinition {
    const targetModel =
        (tool.defaultAiModelId
            ? aiModels.find(model => model.id === tool.defaultAiModelId)
            : undefined) ||
        (tool.defaultAiModelName
            ? aiModels.find(model => model.name === tool.defaultAiModelName)
            : undefined) ||
        null
    const toolWithResolvedDefault = {
        ...tool,
        defaultAiModelId: tool.defaultAiModelId ?? targetModel?.id ?? null,
    }

    return {
        id: tool.id,
        name: tool.key,
        usdCost: targetModel?.usdCost || 0,
        modelType: tool.modelType,
        usdTreechatPrivatePrice: targetModel?.usdTreechatPrivatePrice || 0,
        provider: targetModel?.provider || 'local',
        actionTagName: tool.commandTag,
        usdTreechatPublicPrice: targetModel?.usdTreechatPublicPrice || 0,
        humanName: tool.humanName,
        displayName: tool.displayName || null,
        description: tool.description,
        canRemixVideo: tool.canRemixVideo,
        canRemixImage: tool.canRemixImage,
        canRemixAudio: tool.canRemixAudio,
        sortPriority: tool.sortPriority,
        allowedDurations: targetModel?.allowedDurations,
        minDurationSeconds: targetModel?.minDurationSeconds,
        maxDurationSeconds: targetModel?.maxDurationSeconds,
        defaultDurationSeconds: targetModel?.defaultDurationSeconds,
        usdCostPerSecond: targetModel?.usdCostPerSecond,
        usdTreechatPrivatePricePerSecond: targetModel?.usdTreechatPrivatePricePerSecond,
        usdTreechatPublicPricePerSecond: targetModel?.usdTreechatPublicPricePerSecond,
        estimatedProviderCostUsd: targetModel?.estimatedProviderCostUsd,
        estimatedProviderCostPerSecondUsd: targetModel?.estimatedProviderCostPerSecondUsd,
        pricingMarginBps: targetModel?.pricingMarginBps,
        pricingEstimatorVersion: targetModel?.pricingEstimatorVersion,
        pricingTierKey: targetModel?.pricingTierKey,
        contextualPricingRules: targetModel?.contextualPricingRules,
        referenceImageLimit: tool.referenceImageLimit ?? targetModel?.referenceImageLimit,
        referenceVideoLimit: tool.referenceVideoLimit ?? targetModel?.referenceVideoLimit,
        referenceAudioLimit: tool.referenceAudioLimit ?? targetModel?.referenceAudioLimit,
        kind: 'tool',
        key: `tool:${tool.key}`,
        commandTag: tool.commandTag,
        tool: toolWithResolvedDefault,
        toolKey: tool.key,
        section: tool.section,
        showInWand: tool.showInWand,
        defaultAiModelId: toolWithResolvedDefault.defaultAiModelId,
        supportedInputs: tool.supportedInputs,
        requiredInputs: tool.requiredInputs,
        promptRequired: tool.promptRequired,
        defaultSettings: tool.defaultSettings,
    }
}

function fallbackActionDefinitionFromTag(tag: string): ActionDefinition {
    const modelType = modelTypeForFallbackTag(tag)
    return {
        id: `fallback-model-${tag}`,
        name: tag,
        usdCost: 0,
        modelType,
        usdTreechatPrivatePrice: 0,
        provider: 'fallback',
        actionTagName: tag,
        usdTreechatPublicPrice: 0,
        humanName: tag,
        displayName: actionDisplayNameForModel(
            {
                actionTagName: tag,
            },
            tag,
        ),
        description: '',
        kind: 'model',
        key: `fallback-model:${tag}`,
        commandTag: tag,
        section: sectionForModelType(modelType),
        showInWand: true,
    }
}

export function buildActionDefinitions(aiModels: IAiModel[] = []): ActionDefinition[] {
    const modelActions =
        aiModels.length > 0
            ? aiModels
                  .filter(model => !isHiddenActionCommandTag(model.actionTagName))
                  .map(actionDefinitionFromModel)
            : FALLBACK_MODEL_ACTION_TAGS.filter(tag => !isHiddenActionCommandTag(tag)).map(
                  fallbackActionDefinitionFromTag,
              )

    return [...modelActions, ...TOOL_ACTIONS.map(tool => actionDefinitionFromTool(tool, aiModels))]
}

export function toolActionDispatchMetadataForCommandTag(
    definitions: ActionDefinition[] = [],
    commandTag: string,
): {
    kind: 'tool'
    actionDefinitionId: string
    toolKey: ToolActionKey
    supportsPromptInput: boolean
} | null {
    const normalizedTag = normalizeActionTag(commandTag)
    const definition = definitions.find(
        candidate =>
            candidate.kind === 'tool' && normalizeActionTag(candidate.commandTag) === normalizedTag,
    )

    if (!definition || definition.kind !== 'tool') return null

    return {
        kind: 'tool',
        actionDefinitionId: definition.id,
        toolKey: definition.toolKey,
        supportsPromptInput: Array.isArray(definition.supportedInputs)
            ? definition.supportedInputs.includes('text')
            : definition.promptRequired === true,
    }
}

export function apiActionDefinitionToCamel(
    api: ActionDefinitionApi,
    aiModels: IAiModel[] = [],
): ActionDefinition {
    const kind: ActionKind = api.kind === 'tool' ? 'tool' : 'model'
    const commandTag =
        api.command_tag?.toString() ||
        api.action_tag_name?.toString() ||
        api.tool_key?.toString() ||
        api.name?.toString() ||
        api.id
    const defaultAiModelId =
        api.default_ai_model_id || (kind === 'model' ? api.ai_model_id || api.id : null)

    const baseModelPayload =
        kind === 'model'
            ? {
                  ...api,
                  ...(api.ai_model || {}),
                  id: api.ai_model?.id || api.ai_model_id || api.id,
                  action_tag_name: commandTag,
              }
            : {
                  ...api,
                  id: api.id,
                  name: api.name || api.tool_key || commandTag,
                  human_name: api.human_name || api.name || commandTag,
                  action_tag_name: commandTag,
              }
    const baseModel = apiModelPayloadToCamel(baseModelPayload)
    const defaultAiModel = defaultAiModelId
        ? aiModels.find(model => model.id === defaultAiModelId) || null
        : null
    const tool =
        kind === 'tool'
            ? toolDefinitionFromApi(
                  api.tool || toolApiFromActionDefinitionApi(api),
                  defaultAiModelId,
              )
            : undefined

    const shared = {
        ...baseModel,
        displayName:
            kind === 'tool'
                ? tool?.displayName || baseModel.displayName || null
                : actionDisplayNameForModel(baseModel, commandTag),
        actionTagName: commandTag,
        commandTag,
        section: normalizeSection(api.section) || sectionForModelType(baseModel.modelType),
        showInWand: api.show_in_wand ?? true,
        defaultAiModelId,
        supportedInputs: normalizeActionInputs(api.supported_inputs),
        requiredInputs: api.required_inputs,
        promptRequired: api.prompt_required,
        defaultSettings: camelizeDefaultSettings(api.default_settings),
    }

    if (kind === 'tool') {
        const toolDefinition = tool || toolDefinitionFromApi(toolApiFromActionDefinitionApi(api))
        return {
            ...shared,
            kind: 'tool',
            key: api.key || `tool:${toolDefinition.key || commandTag}`,
            aiModel: defaultAiModel || undefined,
            tool: toolDefinition,
            toolKey: api.tool_key || toolDefinition.key || commandTag,
        }
    }

    return {
        ...shared,
        kind: 'model',
        key: api.key || `model:${baseModel.id}`,
        aiModel: baseModel,
    }
}

export function apiActionDefinitionsToCamel(
    apiDefinitions: ActionDefinitionApi[] = [],
    aiModels: IAiModel[] = [],
): ActionDefinition[] {
    if (!Array.isArray(apiDefinitions)) return []
    return apiDefinitions
        .map(definition => apiActionDefinitionToCamel(definition, aiModels))
        .filter(isVisibleActionDefinition)
}
